import React, { useMemo, useState } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { formatAmount } from "../../../PatientAccounts/utils/patientAccountUtils";
import { convertLineAmount } from "../utils/billingUtils";
import { toMoneyNumber } from "../../../../utils/financeEngine";

export const SECTION_VARIANTS = {
	pharmacy: {
		icon: "fa-medkit",
		accent: "pharmacy",
	},
	services: {
		icon: "fa-stethoscope",
		accent: "services",
	},
	lab: {
		icon: "fa-flask",
		accent: "lab",
	},
	treatment: {
		icon: "fa-heartbeat",
		accent: "treatment",
	},
};

export const CollapsibleSection = ({
	title,
	total,
	currency,
	isOpen,
	onToggle,
	children,
	getCurrencySymbol,
	variant = "pharmacy",
	itemCount = 0,
	hideTotal = false,
}) => {
	const config = SECTION_VARIANTS[variant] || SECTION_VARIANTS.pharmacy;

	return (
		<div
			className={`billing-section-card billing-section-card--${config.accent} ${
				isOpen ? "billing-section-card--open" : ""
			}`}
		>
			<button
				type="button"
				className="billing-section-header"
				onClick={onToggle}
				aria-expanded={isOpen}
			>
				<span className="billing-section-header-main">
					<span className={`billing-section-icon billing-section-icon--${config.accent}`}>
						<i className={`fa ${config.icon}`} />
					</span>
					<span className="billing-section-title">
						{title}
						{itemCount > 0 && (
							<span className="billing-section-count-inline">({itemCount})</span>
						)}
					</span>
				</span>
				<span className="billing-section-meta">
					{!hideTotal && (
						<span className="billing-section-total">
							{formatAmount(total, currency, getCurrencySymbol)}
						</span>
					)}
					<i className={`fa fa-chevron-${isOpen ? "up" : "down"} billing-section-chevron`} />
				</span>
			</button>
			{isOpen && <div className="billing-section-body">{children}</div>}
		</div>
	);
};

export const BillingSectionDivider = ({ title }) => (
	<div className="billing-section-divider">
		<span>{title}</span>
	</div>
);

export const AmountInput = ({
	value,
	onChange,
	onFocus,
	onBlur,
	disabled,
	disableScrollProps,
	formatOnBlur = true,
}) => {
	const handleFocus = (e) => {
		onFocus?.(e);
		disableScrollProps?.onFocus?.(e);
	};

	const handleBlur = (e) => {
		if (onBlur) {
			onBlur(e);
		} else if (formatOnBlur) {
			const num = toMoneyNumber(e.target.value || 0);
			onChange(String(num));
		}
		disableScrollProps?.onBlur?.(e);
	};

	return (
		<Form.Control
			type="text"
			size="sm"
			className="billing-amount-input"
			value={value === 0 || value ? String(value) : ""}
			disabled={disabled}
			onFocus={handleFocus}
			onChange={(e) => {
				const raw = e.target.value;
				if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) {
					onChange(raw);
				}
			}}
			onBlur={handleBlur}
			onKeyDown={disableScrollProps?.onKeyDown}
		/>
	);
};

export const BillingLineQuantityInput = ({ item, updateFn, disabled, disableScrollProps }) => (
	<Form.Control
		type="number"
		size="sm"
		min={1}
		step={1}
		className="billing-line-quantity-input"
		value={item.quantity ?? 1}
		disabled={disabled}
		onChange={(e) => {
			const raw = e.target.value;
			if (raw === "") {
				updateFn(item.id, 1);
				return;
			}
			const val = parseInt(raw, 10);
			updateFn(item.id, Number.isFinite(val) && val > 0 ? val : 1);
		}}
		{...disableScrollProps}
	/>
);

export const BillingLineAmountInput = ({
	item,
	updateFn,
	disabled,
	disableScrollProps,
	displayCurrency,
	convertCurrency,
	pharmacyCurrency,
	allowedCurrencies,
	getCurrencySymbol,
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [draft, setDraft] = useState("");

	const committedValue = useMemo(() => {
		if (!item?.amount && item?.amount !== 0) return "";
		return String(
			convertLineAmount(
				item.amount,
				item.currency,
				displayCurrency,
				convertCurrency,
				pharmacyCurrency,
				allowedCurrencies
			)
		);
	}, [
		allowedCurrencies,
		convertCurrency,
		displayCurrency,
		item?.amount,
		item?.currency,
		pharmacyCurrency,
	]);

	const handleFocus = () => {
		setIsEditing(true);
		setDraft(committedValue);
	};

	const handleBlur = (e) => {
		const rawValue = e?.target?.value ?? draft;
		const displayAmount = toMoneyNumber(rawValue || 0);
		const nativeAmount = convertLineAmount(
			displayAmount,
			displayCurrency,
			item.currency,
			convertCurrency,
			pharmacyCurrency,
			allowedCurrencies
		);
		updateFn(item.id, nativeAmount);
		setIsEditing(false);
		setDraft("");
	};

	const paymentSymbol = getCurrencySymbol(displayCurrency);

	return (
		<InputGroup size="sm" className="billing-line-amount-input-group">
			<InputGroup.Text className="billing-line-currency-symbol" title={displayCurrency}>
				{paymentSymbol}
			</InputGroup.Text>
			<AmountInput
				value={isEditing ? draft : committedValue}
				disabled={disabled}
				formatOnBlur={false}
				onFocus={handleFocus}
				onChange={setDraft}
				onBlur={handleBlur}
				disableScrollProps={disableScrollProps}
			/>
		</InputGroup>
	);
};

export const SourceBadge = ({ variant, children }) => (
	<span className={`billing-source-badge billing-source-badge--${variant}`}>{children}</span>
);

export const BillingAmountCell = ({ children, onRemove, removeLabel }) => (
	<div className="billing-line-amount">
		<div className="billing-line-amount-input">{children}</div>
		{onRemove ? (
			<button
				type="button"
				className="billing-line-remove"
				onClick={onRemove}
				aria-label={removeLabel}
				title={removeLabel}
			>
				<i className="fa fa-trash" />
			</button>
		) : null}
	</div>
);

export const BillingPriorPaymentSummaryRows = ({
	t,
	paymentBalance,
	displayCurrency,
	getCurrencySymbol,
}) => {
	if (!paymentBalance?.hasPriorPayment) return null;

	return (
		<>
			<div className="summary-row billing-summary-row--paid">
				<span>{t("Paid")}</span>
				<strong>
					{formatAmount(paymentBalance.paidSoFar, displayCurrency, getCurrencySymbol)}
				</strong>
			</div>
			<div className="summary-row billing-summary-row--due">
				<span>{t("Balance Due")}</span>
				<strong>
					{formatAmount(paymentBalance.amountDue, displayCurrency, getCurrencySymbol)}
				</strong>
			</div>
		</>
	);
};
